import React, { useState, useEffect } from "react";
import { Plus, Search, UserCog, Edit, Trash2, Loader2, Clock } from "lucide-react";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase/config";
import { useUsers } from "../hooks/useUsers";
import UserModal from "../components/modals/UserModal";
import { validatePassword } from "../utils/passwordPolicy";
import { formatLastLoginDate, formatAuditTimestamp } from "../utils/dateUtils";


export default function Users() {
  const { users, loading: usersLoading, addUser, updateUser, deleteUser } = useUsers();
  const loading = usersLoading;

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");

  // Sync historical logins from audit_logs for any user without a recorded lastLogin
  useEffect(() => {
    if (loading || users.length === 0) return;

    const usersNeedingSync = users.filter(u => !u.lastLogin && !u.lastLoggedIn && u.email);
    if (usersNeedingSync.length === 0) return;

    let isMounted = true;
    async function syncLastLoginsFromAudit() {
      try {
        const q = query(
          collection(db, "audit_logs"),
          where("action", "==", "AUTH_LOGIN_SUCCESS"),
          limit(100)
        );
        const snap = await getDocs(q);
        if (snap.empty || !isMounted) return;

        const emailToLatestLogin = {};
        snap.forEach(docSnap => {
          const data = docSnap.data();
          const email = (data.userEmail || "").toLowerCase();
          const time = data.clientTimestamp || (data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : null);
          if (email && time) {
            if (!emailToLatestLogin[email] || new Date(time) > new Date(emailToLatestLogin[email])) {
              emailToLatestLogin[email] = time;
            }
          }
        });

        for (const u of usersNeedingSync) {
          const email = (u.email || "").toLowerCase();
          const auditTime = emailToLatestLogin[email];
          if (auditTime) {
            updateUser(u.id, {
              lastLogin: auditTime,
              lastLoggedIn: auditTime
            }).catch(e => console.warn("Failed to backfill user last login:", e));
          }
        }
      } catch (err) {
        console.info("Audit log login sync check:", err.message);
      }
    }

    syncLastLoginsFromAudit();
    return () => { isMounted = false; };
  }, [users, loading, updateUser]);
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // If null, we are adding a user
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    roles: [],
    photoURL: ""
  });

  const AVAILABLE_ROLES = [
    "Procurement",
    "Logistics",
    "Warehousing",
    "Admin",
    "Manager",
    "Business Development",
    "Sales",
    "Developer"
  ];

  

  function handleOpenAddModal() {
    setError("");
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      roles: ["Procurement"], // Default role selection
      photoURL: "",
      password: ""
    });
    setModalOpen(true);
  }

  function handleOpenEditModal(user) {
    setError("");
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      roles: user.roles || [],
      photoURL: user.photoURL || ""
    });
    setModalOpen(true);
  }

  const handleRoleToggle = (role) => {
    setFormData(prev => {
      const roles = prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      return { ...prev, roles };
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // Limit size to 2MB
        setError("Image size must be less than 2MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, photoURL: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name.trim() || !formData.email.trim()) {
      setError("Name and Email are required.");
      return;
    }

    if (formData.roles.length === 0) {
      setError("Please assign at least one role to the user.");
      return;
    }

    if (!editingUser) {
      const pwdValidation = validatePassword(formData.password || "");
      if (!pwdValidation.isValid) {
        setError("Password does not meet NIST complexity requirements (12+ characters, uppercase, lowercase, number, special symbol).");
        return;
      }
    }


    try {
      if (editingUser) {
        // Edit existing user
        await updateUser(editingUser.id, formData);
        setSuccess("User updated successfully!");
      } else {
        // Add new user
        await addUser(formData);
        setSuccess("User added successfully!");
      }
      setTimeout(() => {
        setModalOpen(false);
        setSuccess("");
      }, 1500);
    } catch (err) {
      console.error("Error saving user:", err);
      setError("Failed to save user. Please try again.");
    }
  }

  async function handleDeleteUser(userId) {
    if (!window.confirm("Are you sure you want to remove this user from the system?")) return;

    try {
      await deleteUser(userId);
      setSuccess("User deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user.");
    }
  }

  // Filter users list based on search term and role filter
  const filteredUsers = users.filter(user => {
    if (user.deleted || user.status === "Disabled") return false;

    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === "All" || (user.roles && user.roles.includes(roleFilter));
    
    return matchesSearch && matchesRole;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>
            <UserCog className="sidebar-logo-icon" style={{ marginRight: "0.5rem", verticalAlign: "middle" }} />
            System Users & Roles
          </h1>
          <p>Manage user accounts, assign system access permissions, and edit user settings.</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          <Plus size={18} />
          Add User
        </button>
      </div>

      {/* Filters Bar */}
      <div className="search-filter-bar card glass-card" style={{ marginBottom: "1.5rem", padding: "1rem" }}>
        <div className="grid-2" style={{ gap: "1rem", gridTemplateColumns: "1.2fr 0.8fr" }}>
          <div className="search-box">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search users by name or email..."
              className="form-input search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span className="text-secondary" style={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>Filter Role:</span>
            <select
              className="form-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ margin: 0, padding: "0.5rem" }}
            >
              <option value="All">All Roles</option>
              {AVAILABLE_ROLES.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger" style={{ marginBottom: "1.5rem" }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: "1.5rem" }}>{success}</div>}

      {/* Users Table */}
      <div className="card glass-card">
        {loading && users.length === 0 ? (
          <div className="loading-state">
            <Loader2 className="animate-spin" size={32} />
            <p>Syncing users database...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem 1rem", color: "var(--text-muted)" }}>
            <p>No matching users found.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Assigned Roles</th>
                  <th style={{ minWidth: "160px" }}>Last Logged In</th>
                  <th style={{ width: "100px", textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                        {user.photoURL ? (
                          <img src={user.photoURL} alt="Avatar" style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "var(--primary)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "0.85rem", color: "#ffffff" }}>
                            {user.name ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "U"}
                          </div>
                        )}
                        <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{user.name}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                        {user.roles && user.roles.map(role => (
                          <span 
                            key={role} 
                            className="sku-badge" 
                            style={{ 
                            fontSize: "0.75rem", 
                              background: "rgba(33, 150, 243, 0.15)", 
                              color: "var(--primary)" 
                            }}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div 
                        title={user.lastLogin || user.lastLoggedIn ? formatAuditTimestamp(user.lastLogin || user.lastLoggedIn) : "No login recorded"}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <span 
                          style={{ 
                            fontSize: "0.8rem", 
                            fontWeight: (user.lastLogin || user.lastLoggedIn) ? 600 : 400,
                            color: (user.lastLogin || user.lastLoggedIn) ? "var(--text-primary)" : "var(--text-muted)" 
                          }}
                        >
                          {formatLastLoginDate(user.lastLogin || user.lastLoggedIn || user.lastLoginAt || user.lastActive)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                        <button
                          className="btn btn-secondary btn-icon status-action-btn"
                          onClick={() => handleOpenEditModal(user)}
                          title="Edit User"
                          style={{ margin: 0 }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn btn-secondary btn-icon status-action-btn"
                          style={{ borderColor: "var(--danger)", color: "var(--danger)", margin: 0 }}
                          onClick={() => handleDeleteUser(user.id)}
                          title="Remove User"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <UserModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editingUser={editingUser}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        handlePhotoChange={handlePhotoChange}
        AVAILABLE_ROLES={AVAILABLE_ROLES}
        handleRoleToggle={handleRoleToggle}
        error={error}
        success={success}
        loading={loading}
      />
      <style>{`
        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }
        .search-icon {
          position: absolute;
          left: 1rem;
          color: var(--text-muted);
          pointer-events: none;
          z-index: 10;
        }
        .search-input {
          padding-left: 2.75rem !important;
          background-color: var(--bg-input) !important;
          color: var(--text-primary) !important;
          border: 1px solid var(--border-color) !important;
          width: 100%;
        }
        .search-input::placeholder {
          color: var(--text-muted);
        }
      `}</style>
    </div>
  );
}
